import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

class RecentlyViewedProductsSection extends Component {
  /** @type {IntersectionObserver} */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#load();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.rendered === 'true') return;

    const ids = this.#getIds();
    if (ids.length === 0) {
      this.#hide();
      return;
    }

    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#activeFetch?.abort();
  }

  /**
   * @returns {string[]}
   */
  #getIds() {
    const currentProductId = this.dataset.productId;
    return RecentlyViewed.getProducts().filter(
      (/** @type {string} */ id) => id && id !== currentProductId
    );
  }

  async #load() {
    const { sectionId } = this.dataset;
    if (!sectionId) return;

    const ids = this.#getIds();
    if (ids.length === 0) {
      this.#hide();
      return;
    }

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('section_id', sectionId);

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url.toString(), { signal: this.#activeFetch.signal });
      if (!response.ok) {
        this.#hide();
        return;
      }

      const html = await response.text();
      if (!html.trim().length) {
        this.#hide();
        return;
      }

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const newComponent = parsed.getElementById(this.id);
      const hasItems = newComponent?.querySelector('.resource-list__item');

      if (!hasItems) {
        this.#hide();
        return;
      }

      morphSection(sectionId, html, { mode: 'full', injectStylesheet: true });
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Recently viewed products error:', error);
      this.#hide();
    } finally {
      this.#activeFetch = null;
    }
  }

  #hide() {
    this.classList.add('hidden');
    const wrapper = this.closest('.shopify-section');
    if (wrapper) wrapper.classList.add('hidden');
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProductsSection);
}
